export interface CaseType {
  id: number;
  name: string;
  display_name: string;
}

export interface Stage {
  stage_id: number;
  stage_name: string;
  stage_order: number;
  description?: string | null;
  is_active?: boolean;
  color?: string;
  icon?: string;
  value?: string;
}

export interface FieldDef {
  id: number;
  key: string;
  name: string;
  field_type: string;
  db_type?: string;
  case_types?: number[];
  case_type_names?: string[];
  category_name?: string;
  category_id?: number;
  display_order?: number;
  is_required?: boolean;
  source_table?: string;
  options?: { value: string; label: string }[];
  validation_rules?: {
    dynamic?: boolean;
    source?: string;
    readonly?: boolean;
    [key: string]: unknown;
  };
}

export interface FieldOption {
  value: string;
  label?: string;
}

export interface UserPermissions {
  success: boolean;
  allowed_case_types: string[] | null;
}

export interface FilterRule {
  field: string;
  operator: string;
  value?: string | string[];
  andOr?: 'AND' | 'OR';
}

export interface FilterPayload {
  rules: FilterRule[];
  columns?: string[];
  offset: number;
  limit: number;
  searchTerm?: string;
}

export interface FilterResponse {
  cases?: {
    rows: Record<string, unknown>[];
    total: number;
  };
  total?: number;
}

export interface OperatorDef {
  value: string;
  label: string;
}

export const OPERATORS: Record<string, OperatorDef[]> = {
  text: [
    { value: 'eq', label: 'Equals' },
    { value: 'neq', label: 'Not equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'notcontains', label: 'Does not contain' },
    { value: 'startswith', label: 'Starts with' },
    { value: 'endswith', label: 'Ends with' },
    { value: 'in', label: 'In (comma-separated)' },
    { value: 'empty', label: 'Is empty' },
    { value: 'notempty', label: 'Is not empty' },
  ],
  date: [
    { value: 'eq', label: 'Equals' },
    { value: 'neq', label: 'Not equals' },
    { value: 'gt', label: 'After' },
    { value: 'lt', label: 'Before' },
    { value: 'gte', label: 'On or after' },
    { value: 'lte', label: 'On or before' },
    { value: 'empty', label: 'Is empty' },
    { value: 'notempty', label: 'Is not empty' },
  ],
  number: [
    { value: 'eq', label: 'Equals' },
    { value: 'neq', label: 'Not equals' },
    { value: 'gt', label: 'Greater than' },
    { value: 'lt', label: 'Less than' },
    { value: 'gte', label: 'Greater or equal' },
    { value: 'lte', label: 'Less or equal' },
    { value: 'empty', label: 'Is empty' },
    { value: 'notempty', label: 'Is not empty' },
  ],
  boolean: [
    { value: 'eq', label: 'Equals' },
    { value: 'empty', label: 'Is empty' },
    { value: 'notempty', label: 'Is not empty' },
  ],
  dropdown: [
    { value: 'eq', label: 'Equals' },
    { value: 'neq', label: 'Not equals' },
    { value: 'in', label: 'In (multi)' },
    { value: 'empty', label: 'Is empty' },
    { value: 'notempty', label: 'Is not empty' },
  ],
};

export function getFieldCategory(
  fieldDef: FieldDef | undefined,
): 'text' | 'date' | 'number' | 'boolean' | 'dropdown' {
  if (!fieldDef) return 'text';
  const ft = (fieldDef.field_type || '').toLowerCase();
  const dt = (fieldDef.db_type || '').toLowerCase();
  if (ft === 'dropdown' || ft === 'select' || ft === 'select2') return 'dropdown';
  if (fieldDef.validation_rules?.dynamic && fieldDef.validation_rules?.source) return 'dropdown';
  if (
    ['date', 'timestamp', 'timestamptz'].includes(dt) ||
    ['date', 'datetime', 'timestamp'].includes(ft)
  )
    return 'date';
  if (['integer', 'bigint', 'numeric'].includes(dt) || ft === 'number' || ft === 'int')
    return 'number';
  if (dt === 'boolean' || ft === 'boolean' || ft === 'checkbox') return 'boolean';
  return 'text';
}

export interface GlobalSearchResult {
  id?: string;
  ContactID?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  postcode?: string;
  caseRefs?: string;
}
